import { useState, useMemo } from 'react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Input';

export function WordCounter() {
  const [text, setText] = useState(
    'Type or paste text to count words, characters, sentences, and reading time. Markdown is supported.',
  );

  const stats = useMemo(() => {
    const trimmed = text.trim();
    const chars = text.length;
    const charsNoSpace = text.replace(/\s/g, '').length;
    const words = trimmed ? trimmed.split(/\s+/).length : 0;
    const sentences = trimmed ? trimmed.split(/[.!?]+\s/).filter(Boolean).length : 0;
    const paragraphs = trimmed ? trimmed.split(/\n{2,}/).filter(Boolean).length : 0;
    const lines = trimmed ? text.split('\n').length : 0;
    const readingTime = Math.max(1, Math.ceil(words / 220));
    const speakingTime = Math.max(1, Math.ceil(words / 130));
    const longestWord =
      words > 0
        ? trimmed
            .replace(/[^\w\s]/g, '')
            .split(/\s+/)
            .reduce((a, b) => (a.length >= b.length ? a : b), '')
        : '';
    return {
      chars,
      charsNoSpace,
      words,
      sentences,
      paragraphs,
      lines,
      readingTime,
      speakingTime,
      longestWord,
    };
  }, [text]);

  const grid: Array<{ label: string; value: string | number; sub?: string }> = [
    { label: 'Words', value: stats.words.toLocaleString() },
    { label: 'Characters', value: stats.chars.toLocaleString(), sub: `${stats.charsNoSpace.toLocaleString()} excl. spaces` },
    { label: 'Sentences', value: stats.sentences },
    { label: 'Paragraphs', value: stats.paragraphs },
    { label: 'Lines', value: stats.lines },
    { label: 'Reading time', value: `${stats.readingTime}m`, sub: '220 wpm' },
    { label: 'Speaking time', value: `${stats.speakingTime}m`, sub: '130 wpm' },
    { label: 'Longest word', value: stats.longestWord || '—', sub: stats.longestWord ? `${stats.longestWord.length} chars` : undefined },
  ];

  return (
    <div>
      <ToolHeader
        toolId="word-counter"
        title="Word & character counter"
        description="Real-time analysis: words, sentences, paragraphs, reading time, and the longest word."
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {grid.map((g) => (
          <Card key={g.label} className="!p-4">
            <div className="text-2xs uppercase tracking-wider text-fg-subtle">{g.label}</div>
            <div className="text-xl font-semibold mt-1.5 font-mono">{g.value}</div>
            {g.sub && <div className="text-2xs text-fg-subtle mt-0.5">{g.sub}</div>}
          </Card>
        ))}
      </div>

      <Card className="!p-0">
        <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
          Source
        </div>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="!border-0 !rounded-t-none min-h-[280px] !bg-transparent focus-visible:!ring-0"
          placeholder="Paste your text here…"
        />
      </Card>
    </div>
  );
}

export default WordCounter;
